import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Pagination,
  Table,
  Input,
  Tag,
  Tooltip,
  Spin,
  Empty,
  Button,
  ConfigProvider,
  message as antMessage,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  PushpinFilled,
  ArrowLeftOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import { renderMarkdown } from "../../user/ai-copilot/lib/markdown";
import {
  fetchChatUsers,
  fetchUserSessions,
  fetchSessionTranscript,
} from "./lib/api";

const THEME = {
  token: {
    colorPrimary: "#f45b68",
    colorLink: "#f45b68",
    colorLinkHover: "#d94655",
    colorInfo: "#f45b68",
  },
};

function formatDate(ts) {
  if (!ts) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(ts * 1000));
}

function formatRelative(ts) {
  if (!ts) return "";
  const date = new Date(ts * 1000);
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

function initialsOf(name = "") {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

// ─── Users Pane ──────────────────────────────────────────────────────────────
function UsersPane({ token, activeUserId, onSelectUser, onBackToUsers }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");

  const load = useCallback(
    async (override = {}) => {
      setLoading(true);
      try {
        const data = await fetchChatUsers(
          {
            page: override.page ?? page,
            perPage: override.perPage ?? perPage,
            q: override.q !== undefined ? override.q : q,
          },
          token
        );
        setList(data.items);
        setTotal(data.pagination.total || 0);
        setPage(data.pagination.page || override.page || page);
        setPerPage(data.pagination.per_page || override.perPage || perPage);
      } catch (err) {
        antMessage.warning(err?.message || "Failed to load users");
        setList([]);
      } finally {
        setLoading(false);
      }
    },
    [token, page, perPage, q]
  );

  useEffect(() => {
    if (token) load({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSearchDebounced = useMemo(
    () =>
      debounce((val) => {
        setQ(val);
        load({ page: 1, q: val });
      }, 400),
    [load]
  );

  return (
    <aside className="pane users-pane">
      <div className="pane-head">
        <div className="pane-title">
          <span>Users</span>
          <div className="pane-title-actions">
            <span className="count-badge">{total}</span>
            {onBackToUsers ? (
              <Button
                size="small"
                type="text"
                icon={<ArrowLeftOutlined />}
                className="back-users-btn"
                onClick={onBackToUsers}
              >
                Back to Users List
              </Button>
            ) : null}
          </div>
        </div>
        <div className="pane-search">
          <Input
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            placeholder="Search by name or email..."
            onChange={(e) => onSearchDebounced(e.target.value)}
          />
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => load()}
              loading={loading}
            />
          </Tooltip>
        </div>
      </div>

      <div className="pane-body">
        {loading && list.length === 0 ? (
          <div className="pane-loading"><Spin /></div>
        ) : list.length === 0 ? (
          <div className="pane-empty">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No users" />
          </div>
        ) : (
          list.map((u) => (
            <div
              key={u.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectUser(u)}
              onKeyDown={(e) => e.key === "Enter" && onSelectUser(u)}
              className={`user-item ${activeUserId === u.id ? "active" : ""}`}
              title={u.email}
            >
              <div className="u-avatar">{initialsOf(u.name)}</div>
              <div className="u-info">
                <div className="u-name">
                  {u.name || "—"}
                  {u.has_used_ai_chat ? (
                    <span className="active-dot" title="Active AI user" />
                  ) : null}
                </div>
                <div className="u-sub">
                  {u.seller_name ? `${u.seller_name} · ` : ""}
                  {u.email}
                </div>
                <div className="u-meta">
                  <span className="m-pill">
                    {u.session_count ?? 0} sess
                  </span>
                  <span className="m-pill">
                    {u.message_count ?? 0} msg
                  </span>
                  <span className="m-pill">
                    {u.ai_credits_used_this_period ?? 0}/{u.ai_credit_limit ?? 0} credits
                  </span>
                  {u.last_chat_at ? (
                    <span className="m-time" title={formatDate(u.last_chat_at)}>
                      {formatRelative(u.last_chat_at)}
                    </span>
                  ) : (
                    <span className="m-time muted">never</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {total > perPage && (
        <div className="pane-pager">
          <ConfigProvider getPopupContainer={() => document.body}>
            <Pagination
              size="small"
              current={page}
              pageSize={perPage}
              total={total}
              showSizeChanger
              pageSizeOptions={[10, 20, 50, 100]}
              onChange={(p, pp) => {
                setPage(p);
                setPerPage(pp);
                load({ page: p, perPage: pp });
              }}
            />
          </ConfigProvider>
        </div>
      )}
    </aside>
  );
}

// ─── Users Landing Page ──────────────────────────────────────────────────────
function UsersLandingView({ token, onSelectUser }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");

  const load = useCallback(
    async (override = {}) => {
      setLoading(true);
      try {
        const data = await fetchChatUsers(
          {
            page: override.page ?? page,
            perPage: override.perPage ?? perPage,
            q: override.q !== undefined ? override.q : q,
          },
          token
        );
        setList(data.items);
        setTotal(data.pagination.total || 0);
        setPage(data.pagination.page || override.page || page);
      } catch (err) {
        antMessage.warning(err?.message || "Failed to load users");
        setList([]);
      } finally {
        setLoading(false);
      }
    },
    [token, page, perPage, q]
  );

  useEffect(() => {
    if (token) load({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSearchDebounced = useMemo(
    () =>
      debounce((val) => {
        setQ(val);
        load({ page: 1, q: val });
      }, 400),
    [load]
  );

  useEffect(() => {
    return () => {
      onSearchDebounced.cancel();
    };
  }, [onSearchDebounced]);

  const columns = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_, __, idx) => (page - 1) * perPage + idx + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, row) => (
        <div className="d-flex flex-column">
          <span className="fw-bolder text-gray-800" style={{ color: "#f45b68" }}>
            {text || "—"}
          </span>
          <span className="text-muted fs-8">#{row.id}</span>
        </div>
      ),
    },
    {
      title: "Seller Name",
      dataIndex: "seller_name",
      key: "seller_name",
      render: (val) => (val ? val : <span className="text-muted">—</span>),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (val) => <span className="text-gray-700">{val || "—"}</span>,
    },
    {
      title: "Session Count",
      dataIndex: "session_count",
      key: "session_count",
      align: "center",
      render: (val) => <span>{val ?? 0}</span>,
    },
    {
      title: "Message Count",
      dataIndex: "message_count",
      key: "message_count",
      align: "center",
      render: (val) => <span>{val ?? 0}</span>,
    },
    {
      title: "AI Credits (Used/Limit)",
      key: "ai_credits",
      align: "center",
      render: (_, row) => (
        <span>{row.ai_credits_used_this_period ?? 0} / {row.ai_credit_limit ?? 0}</span>
      ),
    },
    {
      title: "Last Chat At",
      dataIndex: "last_chat_at",
      key: "last_chat_at",
      align: "center",
      render: (val) => (val ? formatDate(val) : <span className="text-muted">—</span>),
    },
  ];

  return (
    <div className="users-landing-card">
      <div className="users-landing-head">
        <div>
          <h4 className="fw-bolder text-gray-800 mb-1">Users List</h4>
          <div className="text-muted fs-7">
            Select a user to view sessions and chat transcript.
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined />}
            placeholder="Search by name or email..."
            onChange={(e) => onSearchDebounced(e.target.value)}
            style={{ width: 280 }}
          />
          <Tooltip title="Refresh">
            <Button
              size="large"
              icon={<ReloadOutlined />}
              onClick={() => load()}
              loading={loading}
            />
          </Tooltip>
        </div>
      </div>

      <div className="users-landing-table-wrap">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={list}
          columns={columns}
          pagination={false}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: <Empty description="No users found" /> }}
          onRow={(record) => ({
            onClick: () => onSelectUser(record),
            style: { cursor: "pointer" },
          })}
        />
      </div>

      <div className="users-landing-pager">
        <Pagination
          current={page}
          pageSize={perPage}
          total={total}
          showSizeChanger
          pageSizeOptions={[10, 20, 50, 100]}
          onChange={(p, pp) => {
            setPage(p);
            setPerPage(pp);
            load({ page: p, perPage: pp });
          }}
          showTotal={(t, range) => `${range[0]}-${range[1]} of ${t} users`}
        />
      </div>
    </div>
  );
}

// ─── Sessions Pane ───────────────────────────────────────────────────────────
function SessionsPane({ token, user, selectedSessionId, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 50;

  const load = useCallback(
    async (p = 1) => {
      if (!user?.id) return;
      setLoading(true);
      try {
        const data = await fetchUserSessions(user.id, { page: p, perPage }, token);
        setItems(data.items);
        setTotal(data.pagination.total || 0);
        setPage(data.pagination.page || p);
      } catch (err) {
        antMessage.warning(err?.message || "Failed to load sessions");
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [user?.id, token]
  );

  useEffect(() => {
    if (token && user?.id) load(1);
  }, [token, user?.id, load]);

  const { pinned, others } = useMemo(() => {
    const p = [];
    const o = [];
    items.forEach((s) => (s.is_pinned ? p.push(s) : o.push(s)));
    p.sort((a, b) => (b.pinned_at || 0) - (a.pinned_at || 0));
    o.sort(
      (a, b) =>
        (b.updated_at || b.created_at || 0) -
        (a.updated_at || a.created_at || 0)
    );
    return { pinned: p, others: o };
  }, [items]);

  if (!user) {
    return (
      <aside className="pane sessions-pane">
        <div className="pane-empty pane-empty-full">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a user to view their sessions"
          />
        </div>
      </aside>
    );
  }

  const renderItem = (s) => (
    <div
      key={s.id}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(s)}
      onKeyDown={(e) => e.key === "Enter" && onSelect(s)}
      className={`session-item ${selectedSessionId === s.id ? "active" : ""} ${
        s.is_pinned ? "pinned" : ""
      }`}
    >
      <div className="session-item-head">
        {s.is_pinned ? <PushpinFilled className="pin-icon" /> : null}
        <span className="session-title" title={s.title}>
          {s.title || "Untitled chat"}
        </span>
      </div>
      <div className="session-item-meta">
        <span className="model-tag" title={s.model_used}>
          {s.model_used || "—"}
        </span>
        <span className="msg-count">
          {s.message_count ?? 0} msg{(s.message_count ?? 0) === 1 ? "" : "s"}
        </span>
        <span className="dot-sep" />
        <span className="time">
          {formatRelative(s.updated_at || s.created_at)}
        </span>
      </div>
    </div>
  );

  return (
    <aside className="pane sessions-pane">
      <div className="pane-head">
        <div className="user-block">
          <div className="u-avatar small">{initialsOf(user.name)}</div>
          <div className="u-info">
            <div className="u-name" title={user.name}>{user.name || "—"}</div>
            <div className="u-sub" title={user.email}>
              {user.seller_name ? `${user.seller_name} · ` : ""}{user.email}
            </div>
          </div>
        </div>
        <div className="pane-title sub">
          <span>Sessions</span>
          <span className="count-badge">{total}</span>
        </div>
      </div>

      <div className="pane-body">
        {loading && items.length === 0 ? (
          <div className="pane-loading"><Spin /></div>
        ) : items.length === 0 ? (
          <div className="pane-empty">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chat sessions" />
          </div>
        ) : (
          <>
            {pinned.length > 0 && (
              <>
                <div className="pane-label">Pinned</div>
                {pinned.map(renderItem)}
              </>
            )}
            {others.length > 0 && (
              <>
                {pinned.length > 0 && <div className="pane-label">All Chats</div>}
                {others.map(renderItem)}
              </>
            )}
          </>
        )}
      </div>

      {total > perPage && (
        <div className="pane-pager">
          <Pagination
            simple
            size="small"
            current={page}
            pageSize={perPage}
            total={total}
            onChange={(p) => load(p)}
          />
        </div>
      )}
    </aside>
  );
}

// ─── Message Item ────────────────────────────────────────────────────────────
function MessageItem({ message }) {
  const html = useMemo(() => renderMarkdown(message.content || ""), [message.content]);

  if (message.role === "user") {
    return (
      <div className="msg-row user">
        <div className="bubble user">{message.content}</div>
        <div className="avatar user"><UserOutlined /></div>
      </div>
    );
  }

  return (
    <div className="msg-row assistant">
      <div className="avatar bot">
        <img
          src="/assets/media/sellerView-favicon.png"
          alt="Bot"
          className="bot-logo"
        />
      </div>
      <div className="bubble-wrap">
        <div
          className="bubble bot md-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <div className="bubble-footer">
          <div className="token-info">
            <Tooltip
              title={
                <div>
                  <div>Input tokens: {message.input_tokens ?? 0}</div>
                  <div>Output tokens: {message.output_tokens ?? 0}</div>
                </div>
              }
            >
              <span className="token-pill">
                in {message.input_tokens ?? 0} · out {message.output_tokens ?? 0}
              </span>
            </Tooltip>
            <span className="msg-time">{formatDate(message.created_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Transcript Pane ─────────────────────────────────────────────────────────
function TranscriptPane({ token, user, session }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!session?.id || !user?.id) {
      setData(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setData(null);
      try {
        const res = await fetchSessionTranscript(user.id, session.id, token);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) antMessage.warning(err?.message || "Failed to load transcript");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [user?.id, session?.id, token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [data]);

  if (!user) {
    return (
      <section className="pane transcript-pane">
        <div className="pane-empty pane-empty-full">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Pick a user from the left to begin"
          />
        </div>
      </section>
    );
  }
  if (!session) {
    return (
      <section className="pane transcript-pane">
        <div className="pane-empty pane-empty-full">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a chat session to view the transcript"
          />
        </div>
      </section>
    );
  }

  return (
    <section className="pane transcript-pane">
      <header className="transcript-head">
        <div className="title-block">
          <div className="title-row">
            {session.is_pinned ? (
              <PushpinFilled style={{ color: "#f45b68" }} />
            ) : null}
            <h3 title={data?.session?.title || session.title}>
              {data?.session?.title || session.title || "Untitled chat"}
            </h3>
          </div>
          <div className="sub-row">
            <Tag color="geekblue">
              {data?.session?.model_used || session.model_used}
            </Tag>
            <span className="text-muted fs-8">
              {data?.message_count_total ?? session.message_count ?? 0} messages
            </span>
            <span className="text-muted fs-8">
              Created: {formatDate(data?.session?.created_at || session.created_at)}
            </span>
          </div>
        </div>
      </header>

      <div className="transcript-scroll" ref={scrollRef}>
        {loading ? (
          <div className="pane-loading"><Spin /></div>
        ) : !data?.messages?.length ? (
          <div className="pane-empty pane-empty-full">
            <Empty description="No messages in this session" />
          </div>
        ) : (
          <div className="messages-list">
            {data.messages.map((m) => (
              <MessageItem key={m.id} message={m} />
            ))}
            {data.truncated ? (
              <div className="truncate-note">
                Transcript truncated — only the first{" "}
                {data?.pagination?.per_page || 500} messages shown.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function AIChatSessions(props) {
  const { userData } = props;
  const token = userData?.token;
  const [activeUser, setActiveUser] = useState(null);
  const [activeSession, setActiveSession] = useState(null);

  const handleSelectUser = (u) => {
    setActiveUser(u);
    setActiveSession(null);
  };

  const handleBackToUsersList = () => {
    setActiveSession(null);
    setActiveUser(null);
  };

  const isDrillDown = Boolean(activeUser?.id);
  const hasActiveSession = Boolean(activeSession?.id);

  return (
    <ConfigProvider theme={THEME}>
      <Helmet>
        <title>AI Chat Sessions</title>
      </Helmet>

      <div className="mb-4 mt-2">
        <h2 className="fw-bolder text-gray-900 mb-1">AI Chat Sessions</h2>
      </div>

      <div className="ai-chat-page-body">
        {!isDrillDown ? (
          <UsersLandingView token={token} onSelectUser={handleSelectUser} />
        ) : (
          <div className={`chat-sessions-shell ${hasActiveSession ? "has-session" : "no-session"}`}>
            <UsersPane
              token={token}
              activeUserId={activeUser?.id}
              onSelectUser={handleSelectUser}
              onBackToUsers={handleBackToUsersList}
            />
            <SessionsPane
              token={token}
              user={activeUser}
              selectedSessionId={activeSession?.id}
              onSelect={setActiveSession}
            />
            {hasActiveSession ? (
              <TranscriptPane
                token={token}
                user={activeUser}
                session={activeSession}
              />
            ) : null}
          </div>
        )}
      </div>

      <style>{`
        .ai-chat-page-body {
          min-height: calc(100vh - 112px - var(--app-banner-height, 0px));
          height: calc(100vh - 112px - var(--app-banner-height, 0px));
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .users-landing-card {
          flex: 1;
          min-height: 0;
          background: #fff;
          border: 1px solid #eef0f3;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .users-landing-head {
          padding: 16px 18px;
          border-bottom: 1px solid #eef0f3;
          background: #fafbfc;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .users-landing-table-wrap {
          flex: 1;
          min-height: 0;
          overflow: auto;
          padding: 10px 12px;
        }
        .users-landing-pager {
          border-top: 1px solid #eef0f3;
          padding: 12px 16px;
          display: flex;
          justify-content: flex-end;
          background: #fff;
        }

        .chat-sessions-shell {
          display: grid;
          gap: 12px;
          height: 100%;
          min-height: 0;
        }
        .chat-sessions-shell.no-session {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .chat-sessions-shell.has-session {
          grid-template-columns: 320px 320px 1fr;
        }
        @media (max-width: 1199px) {
          .chat-sessions-shell.no-session {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .chat-sessions-shell.has-session {
            grid-template-columns: 280px 280px 1fr;
          }
        }
        @media (max-width: 991px) {
          .ai-chat-page-body {
            min-height: 0;
            height: auto;
          }
          .users-landing-card {
            height: auto;
            min-height: 0;
          }
          .chat-sessions-shell {
            grid-template-columns: 1fr;
            height: auto;
            min-height: 0;
          }
          .pane { height: 70vh; }
        }

        .pane {
          background: #fff;
          border: 1px solid #eef0f3;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          min-height: 0;
          overflow: hidden;
        }
        .pane-head {
          padding: 12px 14px;
          border-bottom: 1px solid #eef0f3;
          background: #fafbfc;
          flex-shrink: 0;
        }
        .pane-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-weight: 700;
          color: #1f2937;
          font-size: 13px;
          margin-bottom: 8px;
        }
        .pane-title-actions {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .back-users-btn {
          font-size: 11px;
          color: #f45b68 !important;
          font-weight: 600;
          height: 22px;
          padding: 0 6px !important;
        }
        .back-users-btn:hover {
          background: rgba(244, 91, 104, 0.12) !important;
        }
        .pane-title.sub { margin-top: 10px; margin-bottom: 0; font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
        .count-badge {
          background: rgba(244,91,104,0.1);
          color: #f45b68;
          font-size: 11px;
          font-weight: 700;
          padding: 1px 8px;
          border-radius: 10px;
        }
        .pane-search {
          display: flex;
          gap: 6px;
          align-items: center;
        }
        .pane-search .ant-input-affix-wrapper { flex: 1; }
        .pane-body {
          flex: 1;
          overflow-y: auto;
          padding: 6px 8px 10px;
          min-height: 0;
        }
        .pane-pager {
          padding: 8px 12px;
          border-top: 1px solid #eef0f3;
          background: #fff;
          flex-shrink: 0;
        }
        .pane-loading,
        .pane-empty { padding: 30px 12px; text-align: center; }
        .pane-empty-full {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #6b7280;
        }
        .pane-label {
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          padding: 10px 10px 6px;
        }

        /* Users list */
        .user-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px 10px;
          border-radius: 8px;
          cursor: pointer;
          margin-bottom: 4px;
          border: 1px solid transparent;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .user-item:hover { background: #fafbfc; border-color: #eef0f3; }
        .user-item.active {
          background: rgba(244,91,104,0.08);
          border-color: rgba(244,91,104,0.3);
        }
        .u-avatar {
          width: 34px; height: 34px;
          border-radius: 50%;
          background: rgba(244,91,104,0.12);
          color: #f45b68;
          font-weight: 700;
          font-size: 12px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .u-avatar.small { width: 30px; height: 30px; font-size: 11px; }
        .u-info { min-width: 0; flex: 1; }
        .u-name {
          font-weight: 700;
          color: #1f2937;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .active-dot {
          width: 6px; height: 6px;
          background: #22c55e;
          border-radius: 50%;
          display: inline-block;
        }
        .u-sub {
          color: #6b7280;
          font-size: 11px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .u-meta {
          display: flex;
          gap: 6px;
          margin-top: 6px;
          align-items: center;
          flex-wrap: wrap;
        }
        .m-pill {
          background: #eef0f3;
          color: #4b5563;
          padding: 1px 7px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 600;
        }
        .m-time { font-size: 10px; color: #6b7280; }
        .m-time.muted { color: #9ca3af; font-style: italic; }

        /* Sessions list */
        .user-block {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .user-block .u-info .u-name { font-size: 13px; }
        .session-item {
          padding: 10px 12px;
          border-radius: 8px;
          cursor: pointer;
          margin-bottom: 4px;
          transition: background 0.15s ease, border-color 0.15s ease;
          border: 1px solid transparent;
          background: #fff;
        }
        .session-item:hover { background: #fafbfc; border-color: #eef0f3; }
        .session-item.active {
          background: rgba(244,91,104,0.08);
          border-color: rgba(244,91,104,0.3);
        }
        .session-item.pinned { border-left: 3px solid #f45b68; }
        .session-item-head { display: flex; align-items: center; gap: 6px; }
        .session-item-head .pin-icon { color: #f45b68; font-size: 12px; }
        .session-title {
          font-weight: 600;
          color: #1f2937;
          font-size: 13px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
        }
        .session-item-meta {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 10px;
          color: #6b7280;
          margin-top: 4px;
          flex-wrap: wrap;
        }
        .model-tag {
          background: #eef0f3;
          color: #4b5563;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 10px;
          max-width: 140px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .dot-sep {
          width: 3px; height: 3px;
          border-radius: 50%;
          background: #d1d5db;
        }

        /* Transcript */
        .transcript-pane { background: #f7f8fa; }
        .transcript-head {
          padding: 14px 20px;
          border-bottom: 1px solid #eef0f3;
          background: #fff;
          flex-shrink: 0;
        }
        .title-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .title-row h3 {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
          color: #1f2937;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .sub-row {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 6px;
          flex-wrap: wrap;
        }
        .transcript-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 20px 24px;
          min-height: 0;
        }
        .messages-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          max-width: min(1280px, 100%);
          margin: 0 auto;
          width: 100%;
        }
        .msg-row { display: flex; gap: 10px; align-items: flex-start; }
        .msg-row.user { justify-content: flex-end; }
        .avatar {
          width: 32px; height: 32px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          font-size: 14px;
        }
        .avatar.bot { background: #1f2937; color: #fff; }
        .avatar.bot {
          background: linear-gradient(135deg, #314f71, #44688f);
          border: 1px solid #dce6f2;
          box-shadow: 0 4px 10px rgba(49, 79, 113, 0.12);
          overflow: hidden;
        }
        .avatar.bot .bot-logo {
          width: 20px;
          height: 20px;
          object-fit: cover;
          filter: brightness(0) invert(1);
        }
        .avatar.user { background: rgba(244,91,104,0.12); color: #f45b68; }
        .bubble {
          padding: 12px 14px;
          border-radius: 14px;
          font-size: 13.5px;
          line-height: 1.6;
          word-wrap: break-word;
        }
        .bubble.user {
          background: #f45b68;
          color: #fff;
          border-top-right-radius: 4px;
          white-space: pre-wrap;
          max-width: min(62%, 760px);
        }
        .bubble.bot {
          background: #fff;
          color: #1f2937;
          border: 1px solid #eef0f3;
          border-top-left-radius: 4px;
          width: 100%;
          max-width: none;
        }
        .bubble-wrap {
          display: flex;
          flex-direction: column;
          min-width: 0;
          width: min(980px, calc(100% - 56px));
          max-width: min(980px, calc(100% - 56px));
        }
        .bubble-footer {
          margin-top: 6px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 10px;
          flex-wrap: wrap;
        }
        .token-info {
          display: flex;
          gap: 10px;
          align-items: center;
          color: #6b7280;
          font-size: 11px;
          flex-wrap: wrap;
        }
        .token-pill {
          background: #eef0f3;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          color: #4b5563;
        }
        .msg-time { font-size: 11px; }

        .md-content h1,
        .md-content h2,
        .md-content h3 { font-weight: 700; margin: 10px 0 6px; }
        .md-content p { margin: 6px 0; }
        .md-content ul, .md-content ol { margin: 6px 0 6px 18px; }
        .md-content code {
          background: #f1f3f5;
          padding: 1px 5px;
          border-radius: 4px;
          font-size: 12px;
        }
        .md-content pre {
          background: #1f2937;
          color: #f3f4f6;
          padding: 10px 12px;
          border-radius: 8px;
          overflow-x: auto;
          font-size: 12px;
        }
        .md-content table {
          border-collapse: collapse;
          margin: 10px 0;
          width: 100%;
          font-size: 12px;
        }
        .md-content th, .md-content td {
          border: 1px solid #e5e7eb;
          padding: 6px 10px;
          text-align: left;
        }
        .md-content th { background: #f3f4f6; font-weight: 700; }
        .md-content blockquote {
          border-left: 3px solid #f45b68;
          padding-left: 10px;
          color: #4b5563;
          margin: 8px 0;
        }
        .truncate-note {
          text-align: center;
          font-size: 11px;
          color: #9ca3af;
          margin-top: 10px;
        }
      `}</style>
    </ConfigProvider>
  );
}
